import { useState, useEffect, useRef } from 'react'
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  EyeIcon,
  CheckIcon,
  UserIcon,
  CalendarIcon,
  ArrowPathIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import { FlagIcon as FlagSolid } from '@heroicons/react/24/solid'
import UserAvatar from '../../components/UserAvatar'

interface Message {
  id: string
  senderId: string
  receiverId: string
  content: string
  type: string
  status: 'sent' | 'delivered' | 'read'
  createdAt: string
  readAt?: string
  flaggedByAdmin: boolean
  flagReason?: string
  isSensitive: boolean
  moderationStatus: 'pending' | 'approved' | 'rejected'
  adminNotes?: string
  deletedBySender: boolean
  deletedByReceiver: boolean
  sender: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    email: string
    role: string
  }
  receiver: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    email: string
    role: string
  }
  booking?: {
    id: string
    course?: {
      title: string
    }
  }
}

interface Conversation {
  user1: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    email: string
    role: string
  }
  user2: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    email: string
    role: string
  }
  messageCount: number
  lastMessageTime: string
}

interface Stats {
  totalMessages: number
  todayMessages: number
  flaggedMessages: number
  sensitiveMessages: number
  pendingModeration: number
  activeUsersToday: number
}

const AdminMessages = () => {
  const [messages, setMessages] = useState<Message[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [selectedConversation, setSelectedConversation] = useState<{ user1Id: string; user2Id: string } | null>(null)
  const [conversationMessages, setConversationMessages] = useState<Message[]>([])
  const [loadingConversation, setLoadingConversation] = useState(false)
  
  // 过滤条件
  const [filters, setFilters] = useState({
    keyword: '',
    flagged: false,
    sensitive: false,
    moderationStatus: '',
    startDate: '',
    endDate: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // 标记弹窗
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [flagReason, setFlagReason] = useState('')
  const [adminNotes, setAdminNotes] = useState('')

  useEffect(() => {
    fetchStats()
    fetchMessages()
  }, [page, filters])

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/messages/admin/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (error) {
      console.error('获取统计数据失败:', error)
    }
  }

  const fetchMessages = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      })
      
      if (filters.keyword) params.append('keyword', filters.keyword)
      if (filters.flagged) params.append('flagged', 'true')
      if (filters.sensitive) params.append('sensitive', 'true')
      if (filters.moderationStatus) params.append('moderationStatus', filters.moderationStatus)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)

      const response = await fetch(`/api/messages/admin/all?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setMessages(data.messages || [])
        setTotalPages(data.totalPages || 1)
      }
    } catch (error) {
      console.error('获取消息失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchConversation = async (user1Id: string, user2Id: string) => {
    setLoadingConversation(true)
    setSelectedConversation({ user1Id, user2Id })
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/messages/admin/conversation/${user1Id}/${user2Id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setConversationMessages(data.messages || [])
      }
    } catch (error) {
      console.error('获取对话失败:', error)
    } finally {
      setLoadingConversation(false)
    }
  }

  const flagMessage = async (messageId: string, flagged: boolean, reason?: string, notes?: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/messages/admin/flag/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          flagged,
          reason,
          adminNotes: notes
        })
      })
      
      if (response.ok) {
        fetchMessages()
        fetchStats()
        setShowFlagModal(false)
        setFlagReason('')
        setAdminNotes('')
        setSelectedMessage(null)
      }
    } catch (error) {
      console.error('标记消息失败:', error)
    }
  }

  const updateModerationStatus = async (messageId: string, status: 'approved' | 'rejected') => {
    try {
      const token = localStorage.getItem('token')
      await fetch(`/api/messages/admin/flag/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ moderationStatus: status })
      })
      fetchMessages()
      fetchStats()
    } catch (error) {
      console.error('更新审核状态失败:', error)
    }
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRoleBadge = (role: string) => {
    const config: Record<string, { label: string; className: string }> = {
      student: { label: '学生', className: 'bg-blue-50 text-blue-700' },
      teacher: { label: '教师', className: 'bg-emerald-50 text-emerald-700' },
      admin: { label: '管理员', className: 'bg-purple-50 text-purple-700' }
    }
    const { label, className } = config[role] || { label: role, className: 'bg-slate-50 text-slate-700' }
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
      {/* 头部 */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-200/50">
                <ChatBubbleLeftRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">消息监控中心</h1>
                <p className="text-xs sm:text-sm text-slate-500">监控和管理平台所有消息</p>
              </div>
            </div>
            <button
              onClick={() => { fetchMessages(); fetchStats(); }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-700 transition"
            >
              <ArrowPathIcon className="w-4 h-4" />
              刷新
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 统计卡片 */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-2xl font-bold text-slate-900">{stats.totalMessages}</div>
              <div className="text-xs text-slate-500">总消息数</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.todayMessages}</div>
              <div className="text-xs text-slate-500">今日消息</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-2xl font-bold text-amber-600">{stats.flaggedMessages}</div>
              <div className="text-xs text-slate-500">已标记</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-2xl font-bold text-rose-600">{stats.sensitiveMessages}</div>
              <div className="text-xs text-slate-500">敏感内容</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-2xl font-bold text-purple-600">{stats.pendingModeration}</div>
              <div className="text-xs text-slate-500">待审核</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="text-2xl font-bold text-emerald-600">{stats.activeUsersToday}</div>
              <div className="text-xs text-slate-500">今日活跃用户</div>
            </div>
          </div>
        )}

        {/* 过滤器 */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={filters.keyword}
                  onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                  placeholder="搜索消息内容..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                />
              </div>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                showFilters ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              筛选
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            <button
              onClick={() => setFilters({ ...filters, flagged: !filters.flagged })}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                filters.flagged ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <FlagIcon className="w-4 h-4" />
              已标记
            </button>

            <button
              onClick={() => setFilters({ ...filters, sensitive: !filters.sensitive })}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                filters.sensitive ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <ExclamationTriangleIcon className="w-4 h-4" />
              敏感内容
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">审核状态</label>
                <select
                  value={filters.moderationStatus}
                  onChange={(e) => setFilters({ ...filters, moderationStatus: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">全部</option>
                  <option value="pending">待审核</option>
                  <option value="approved">已通过</option>
                  <option value="rejected">已拒绝</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">开始日期</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">结束日期</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* 消息列表 */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <ChatBubbleLeftRightIcon className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">暂无消息</p>
              <p className="text-sm">没有符合筛选条件的消息</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-4 hover:bg-slate-50 transition ${
                    msg.flaggedByAdmin ? 'bg-amber-50/50' : ''
                  } ${msg.isSensitive ? 'bg-rose-50/50' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    {/* 发送者 */}
                    <div className="flex items-center gap-2 min-w-[180px]">
                      <UserAvatar
                        avatar={msg.sender?.avatar}
                        firstName={msg.sender?.firstName}
                        lastName={msg.sender?.lastName}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {msg.sender?.firstName} {msg.sender?.lastName}
                        </div>
                        <div className="flex items-center gap-1">
                          {getRoleBadge(msg.sender?.role)}
                        </div>
                      </div>
                    </div>

                    {/* 箭头 */}
                    <div className="text-slate-300 mt-2">→</div>

                    {/* 接收者 */}
                    <div className="flex items-center gap-2 min-w-[180px]">
                      <UserAvatar
                        avatar={msg.receiver?.avatar}
                        firstName={msg.receiver?.firstName}
                        lastName={msg.receiver?.lastName}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {msg.receiver?.firstName} {msg.receiver?.lastName}
                        </div>
                        <div className="flex items-center gap-1">
                          {getRoleBadge(msg.receiver?.role)}
                        </div>
                      </div>
                    </div>

                    {/* 消息内容 */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 line-clamp-2">{msg.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">{formatTime(msg.createdAt)}</span>
                        {msg.booking?.course && (
                          <span className="text-xs text-purple-600">
                            📚 {msg.booking.course.title}
                          </span>
                        )}
                        {msg.deletedBySender && (
                          <span className="text-xs text-rose-500">发送者已删除</span>
                        )}
                        {msg.deletedByReceiver && (
                          <span className="text-xs text-rose-500">接收者已删除</span>
                        )}
                      </div>
                      {msg.flagReason && (
                        <div className="mt-1 text-xs text-amber-600">
                          标记原因: {msg.flagReason}
                        </div>
                      )}
                    </div>

                    {/* 状态标签 */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {msg.flaggedByAdmin && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          <FlagSolid className="w-3 h-3" />
                          已标记
                        </span>
                      )}
                      {msg.isSensitive && (
                        <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-full text-xs font-medium">
                          敏感
                        </span>
                      )}
                      {msg.moderationStatus === 'pending' && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                          待审核
                        </span>
                      )}
                      {msg.moderationStatus === 'rejected' && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                          已拒绝
                        </span>
                      )}
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => fetchConversation(msg.senderId, msg.receiverId)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition"
                        title="查看完整对话"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMessage(msg)
                          setFlagReason(msg.flagReason || '')
                          setAdminNotes(msg.adminNotes || '')
                          setShowFlagModal(true)
                        }}
                        className={`p-2 rounded-lg transition ${
                          msg.flaggedByAdmin 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                        }`}
                        title={msg.flaggedByAdmin ? '编辑标记' : '标记消息'}
                      >
                        <FlagIcon className="w-4 h-4" />
                      </button>
                      {msg.moderationStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => updateModerationStatus(msg.id, 'approved')}
                            className="p-2 hover:bg-emerald-100 rounded-lg text-slate-500 hover:text-emerald-700 transition"
                            title="通过"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => updateModerationStatus(msg.id, 'rejected')}
                            className="p-2 hover:bg-rose-100 rounded-lg text-slate-500 hover:text-rose-700 transition"
                            title="拒绝"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-100">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <span className="text-sm text-slate-600">
                第 {page} / {totalPages} 页
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 对话详情弹窗 */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full h-[80vh] max-h-[700px] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">对话详情</h3>
              <button
                onClick={() => {
                  setSelectedConversation(null)
                  setConversationMessages([])
                }}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition"
              >
                <XMarkIcon className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {loadingConversation ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                </div>
              ) : conversationMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 opacity-50" />
                  <p>暂无消息</p>
                </div>
              ) : (
                conversationMessages.map((msg) => (
                  <div key={msg.id} className="flex items-start gap-3">
                    <UserAvatar
                      avatar={msg.sender?.avatar}
                      firstName={msg.sender?.firstName}
                      lastName={msg.sender?.lastName}
                      size="sm"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-900">
                          {msg.sender?.firstName} {msg.sender?.lastName}
                        </span>
                        {getRoleBadge(msg.sender?.role)}
                        <span className="text-xs text-slate-400">{formatTime(msg.createdAt)}</span>
                        {msg.flaggedByAdmin && (
                          <FlagSolid className="w-3 h-3 text-amber-500" />
                        )}
                      </div>
                      <div className={`p-3 rounded-xl ${
                        msg.flaggedByAdmin ? 'bg-amber-50 border border-amber-200' :
                        msg.isSensitive ? 'bg-rose-50 border border-rose-200' :
                        'bg-white border border-slate-200'
                      }`}>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {(msg.deletedBySender || msg.deletedByReceiver) && (
                        <div className="text-xs text-rose-500 mt-1">
                          {msg.deletedBySender && '发送者已删除'} {msg.deletedByReceiver && '接收者已删除'}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* 标记弹窗 */}
      {showFlagModal && selectedMessage && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              {selectedMessage.flaggedByAdmin ? '编辑标记' : '标记消息'}
            </h3>
            
            <div className="mb-4 p-3 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-700 line-clamp-3">{selectedMessage.content}</p>
              <p className="text-xs text-slate-400 mt-2">
                {selectedMessage.sender?.firstName} {selectedMessage.sender?.lastName} → 
                {selectedMessage.receiver?.firstName} {selectedMessage.receiver?.lastName}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">标记原因</label>
              <input
                type="text"
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                placeholder="请输入标记原因..."
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-1">管理员备注</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="内部备注..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 resize-none"
              />
            </div>

            <div className="flex gap-3">
              {selectedMessage.flaggedByAdmin && (
                <button
                  onClick={() => flagMessage(selectedMessage.id, false)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition"
                >
                  取消标记
                </button>
              )}
              <button
                onClick={() => setShowFlagModal(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition"
              >
                取消
              </button>
              <button
                onClick={() => flagMessage(selectedMessage.id, true, flagReason, adminNotes)}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-medium shadow-lg shadow-amber-200/50 hover:shadow-xl transition"
              >
                确认标记
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminMessages

